import type { App } from 'vue';
import type { I18n } from 'vue-i18n';
import { getGlobalNamespace, interceptAxios } from '@patsnap-utils/passport';
import { setPostByFormPassport, psAxios } from '@patsnap/common-ps-http';
import type Passport from '@patsnap-utils/passport/src/passport';
import GoogleSignInPlugin from 'vue3-google-signin'
import { cn, en, jp, tw } from './src/locales'
import { getCurrentLanguage } from '@eureka/language-tool'
import _ from 'lodash';
import { handleThirdPartyLoginErrors } from './src/login-us/utils/third-login-error-handler';
export interface OpenCallbackParams {
    token: string
    refresh_token: string
    passport: Passport
}

export enum DialogMode {
    LOGIN = 'login',
    REGISTER = 'register'
}

export enum WUJIE_PROPS_USER_ENTRY_METHOD {
    /**
     * 微信登录注册
     */
    Wechat = 'wechat',
    /**
     * 手机号登录注册
     */
    SMS = 'sms',
    /**
     * 账号密码登录
     */
    Account = 'account',
    /**
     * sso carsi/saml登录注册
     */
    SSO = 'sso',
    /**
     * google登录
     */
    Google = 'google',
    /**
     * 免费注册
     */
    FreeRegister = 'freeRegister'
}

export interface OpenFallbackParams {
    close?: boolean
    error?: Error
}

export enum SDK_ENV {
    CN = 'CN',
    US = 'US',
    EU = 'EU'
}

export interface LoginSDKOptions {
    i18n: I18n
    env: SDK_ENV
    channels: Channels
}

interface OpenOptions {
    channels?: Channels
    /** 隐藏关闭按钮 */
    withoutClose?: boolean
    /** 是否是静默模式,静默模式弹窗位置为显示在右下角,且没有遮罩框 */
    silence?: boolean
}

export interface Channels {
    from?: string
    from_page?: string
    channel_source?: string
    activity_type?: string
    inviter_id?: string
    invitation_code?: string
    anonymousLogin?: boolean
}

export enum EMPOWER_TYPE {
    AIR_HOME = 'AIR_HOME',
    FMEA = 'FMEA',
    MONITOR = 'MONITOR',
    TECH_RESEARCH = 'TECH_RESEARCH',
    TECH_MIND = 'TECH_MIND',
    MATERIAL_MIND = 'MATERIAL_MIND',
    HISTORY = 'HISTORY'
}

export enum CHANNEL_SOURCE {
    AIR_EUREKA = 'dialog_signup_eureka'
}

// LoginResult 类型定义
interface LoginResult {
    url?: string // 登录成功后的跳转 URL
    [key: string]: any // 其他可能的登录相关数据
}

interface ILoginSDK {
openLoginDialog: (options?: { channels?: Channels, silence?: boolean, withoutClose?: boolean }) => Promise<LoginResult>
initSDK: () => Promise<void>
}

let loginSDKPromise: Promise<ILoginSDK>

export function getEurekaLoginSDK(): Promise<ILoginSDK> {
    if (loginSDKPromise) {
        return loginSDKPromise
    }
    const sdk = loginSDKPromise ??= import(/* @vite-ignore */`${import.meta.env.VITE_EUREKA_URI}/login/login.sdk.es.no-cache.js`)
    return sdk as Promise<ILoginSDK>
}

export class LoginSDK {
    private i18n: I18n | undefined = undefined;
    private env: SDK_ENV = SDK_ENV.CN
    private channels: Channels = {};
    private _div: HTMLDivElement | null = null;
    private _html: HTMLHtmlElement | null = null;
    private _app: App<Element> | null = null;

    constructor(options?: LoginSDKOptions | undefined) {
        if (options?.i18n) {
            this.i18n = options.i18n
        }
        if (options?.env) {
            this.env = options.env
        }
        if (options?.channels) {
            this.channels = options.channels
        }

        return this
    }

    private checkI18n() {
        if (!this.i18n) {
            throw new Error('i18n is required')
        }
    }

    getI18n() {
        return this.i18n
    }

    setI18n(i18n: I18n) {
        this.i18n = i18n
        const locale = getCurrentLanguage()
        const messages = {
            cn,
            en,
            jp,
            tw,
        }
        this.i18n.global.mergeLocaleMessage(locale, _.get(messages, locale))
        return this
    }

    setEnv(env: SDK_ENV) {
        this.env = env
        return this
    }

    setChannels(channels: Channels) {
        this.channels = channels
        return this
    }

    async getLoginUI() {
        return this.env === SDK_ENV.CN
            ? await import('./src/LoginCN.vue').then(m => m.default)
            : await import('./src/LoginUS.vue').then(m => m.default)
    }

    private _createContainer() {
        this._html = window.parent.document.querySelector('html')
        this._html?.setAttribute('style', 'overflow:hidden')
        if (this._div) {
            this._div.remove()
        }
        this._div = document.createElement('div');
        this._div.setAttribute('data-theme', 'patsnapRD');
        this._div.style.cssText = 'display:flex;align-items:center;justify-content:center;position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.5);z-index:99999;';
        document.body.appendChild(this._div);
        return this._div;
    }

    private _destroyContainer() {
        this._div?.remove();
        this._app?.unmount();
        this._html?.removeAttribute('style');
        return this;
    }

    private async _prepareTokenBase() {
        const { singletonPassport: passport } = getGlobalNamespace()
        const token = await passport.getAccessToken()
        if (!psAxios.psInterceptors.passportRequestInterceptor) {
            interceptAxios(psAxios);
        }
        setPostByFormPassport(passport)
        return {
            token,
            passport,
        }
    }

    private _registerGoogleLogin(app: App) {
        app.use(GoogleSignInPlugin, {
        // @ts-ignore
            clientId: import.meta.env.VITE_GOOGLE_LOGIN_CLIENT_ID,
        });
    }

    async open(options?: OpenOptions) {
        this.checkI18n();
        return this.openWithExternalSDK(options)
    }

    /**
     * 处理第三方登录错误(linkedin, microsoft)
     */
    handleThirdPartyLoginErrors() {
        if (this.env === SDK_ENV.CN) return;
        handleThirdPartyLoginErrors(this.i18n);
    }

    close() {
        this._destroyContainer();
    }

    /**
     * 使用外部 SDK 打开登录对话框
     * @param options 登录选项
     */
    async openWithExternalSDK(options?: OpenOptions): Promise<void> {
        // 动态加载外部 SDK
        const { openLoginDialog } = await getEurekaLoginSDK() as unknown as ILoginSDK;

        // 调用外部 SDK 打开登录对话框
        await openLoginDialog({
            ...options,
            channels: {
                from: 'eureka',
                anonymousLogin: false,
                ...this.channels,
            },
        });
    }
}

export const loginSDK = new LoginSDK();

export function buildQueryChannels(queryObj: Channels | undefined) {
    if (!queryObj) return ''
    const results: string[] = []
    for (const key in queryObj) {
        const value = queryObj[key as keyof Channels]
        if (value !== undefined) {
            results.push(`${key}=${value}`)
        }
    }
    return results.join('&')
}
